import React, { useEffect, useRef } from "react";

import { DealsSection } from "@/types/deals.types";
import useArticleSection from "@FillInTableContainer/hooks/useArticleSection";
import TableCreatorDragButton from "@FillInTableContainer/shared/components/TableCreatorDragButton/TableCreatorDragButton";
import { TableCreatorShellList } from "@FillInTableContainer/shared/types/tableCreator.types";
import { isAddDeleteSubGroupShell } from "@FillInTableContainer/shared/utils/getOptionsFromData";
import IconXMark from "@images/fillInTable/tableCreator/icon-x-mark-white.svg";

import DealsKeyCommentCreatorShell from "../../../../../DealsKeyCommentCreatorTable/components/DealsKeyCommentCreatorGroupShell/components/DealsKeyCommentCreatorShell/DealsKeyCommentCreatorShell";
import * as S from "./DealsKeyCommentMaSubGroupShell.style";
import { useTableCreatorMergeAcquisitionSubGroupShell } from "../../../../../../../DealsDealTermsTableCreatorContainer/components/DealTermsMergeGroupTable/components/DealTermsMergeTable/components/DealTermsMergeGroupShell/components/DealTermsMergeSubGroupShell/hooks/useTableCreatorMergeAcquisitionSubGroupShell";
import SubGroupShellDividerPlusBar from "../../../../../../../DealsDealTermsTableCreatorContainer/components/DealTermsLicenseAgreementGroupTable/components/DealTermsLicenseAgreementTable/components/DealTermsLicenseAgreementGroupShellSection/components/DealTermsLicenseAgreementGroupShell/components/DealTermsLicenseAgreementSubGroupShell/components/SubGroupShellDividerPlusBar/SubGroupShellDividerPlusBar";

interface Props {
  groupTableKey: DealsSection;
  groupTableIndex: number;
  groupShellKey: string | null;
  groupShellIndex: number;
  subGroupShellKey: string | null;
  subGroupShellIndex: number;
  subGroupShellCount: number;
  shellList: TableCreatorShellList;
  tableKey: string | null;
  tableIndex: number;
  handleDragStart: (
    event: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => void;
  handleDragEnd: (subGroupShellKey: string) => void;
  handleDragOn: () => void;
}

const DealsKeyCommentMaSubGroupShell = ({
  groupTableKey,
  groupTableIndex,
  groupShellKey,
  groupShellIndex,
  subGroupShellKey,
  subGroupShellIndex,
  subGroupShellCount,
  shellList,
  tableKey,
  tableIndex,
  handleDragEnd,
  handleDragOn,
  handleDragStart,
}: Props) => {
  const positionPropsInfo = {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    subGroupShellKey,
    subGroupShellIndex,
    tableKey,
    tableIndex,
  };

  const articleSection = useArticleSection();
  const selectButtonRef = useRef<HTMLDivElement>(null);

  const {
    focusStatus,
    isFocused,
    isEnableEdit,
    isClearSubGroupShell,
    isDeleteSubGroupShell,
    isLastSubGroupShell,
    isFocusedAddButton,
    handleClickToDeleteSubGroupShell,
    handleFocusSubGroupShell,
    clickAddSubGroupShellButton,
    handleDefaultFocus,
    changeFocusStatus,
    changePositionInfo,
    changePositionInfoToDefault,
    keydownClearOrDeleteSubGroupShellData,
  } = useTableCreatorMergeAcquisitionSubGroupShell({
    positionPropsInfo,
    subGroupShellCount,
  });

  useEffect(() => {
    const handleCheckClickOutsideShell = (e: MouseEvent) => {
      if (isFocused) {
        const target = e.target as HTMLElement;
        const clickSelectButton = selectButtonRef.current?.contains(target);
        if (clickSelectButton) return;
        if (target.dataset.button) return;
        handleDefaultFocus();
      }
    };

    window.addEventListener("click", handleCheckClickOutsideShell);
    return () =>
      window.removeEventListener("click", handleCheckClickOutsideShell);
  }, [handleDefaultFocus, isFocused]);

  const changeFocusStatusForDragging = () => {
    changePositionInfo();
    changeFocusStatus({ type: "dragging", container: "subGroupShell" });
  };

  const changeFocusStatusForDragEnd = () => {
    changePositionInfoToDefault();
    changeFocusStatus({ type: "default", container: "noFocus" });
  };

  const isDraggingSubGroupShell =
    isEnableEdit && subGroupShellCount > 1;

  const isDisplayClearDeleteButton =
    (isClearSubGroupShell || isDeleteSubGroupShell) && isEnableEdit;

  return (
    <>
      {isAddDeleteSubGroupShell(articleSection, subGroupShellKey) && (
        <SubGroupShellDividerPlusBar
          onClick={(event) => {
            clickAddSubGroupShellButton(event, {
              tableIndexParameter: tableIndex,
              groupShellIndexParameter: groupShellIndex,
              groupShellKeyParameter: groupShellKey,
              subGroupShellIndexParameter: subGroupShellIndex,
              subGroupShellKeyParameter: subGroupShellKey,
            });
          }}
          isLastPosition={false}
          isVisibility={isFocusedAddButton}
          subGroupShellKey={subGroupShellKey}
        />
      )}
      <S.GroupShellOutsideWrapper
        onKeyDown={keydownClearOrDeleteSubGroupShellData}
      >
        {isDisplayClearDeleteButton && (
          <S.DeleteTableButtonWrapper $isDragging={isDraggingSubGroupShell}>
            <S.DeleteTableButton
              data-button="delete-button"
              onClick={handleClickToDeleteSubGroupShell}
            >
              <img src={IconXMark} alt="button for clear Row" />
              {focusStatus.type === "clear" ? `Clear` : null}
              {focusStatus.type === "delete" ? `Delete` : null}
            </S.DeleteTableButton>
          </S.DeleteTableButtonWrapper>
        )}
        <S.GroupShellWrapper
          isFocused={isFocused}
          isDragging={isFocused && focusStatus.type === "dragging"}
        >
          <S.SelectWrapper isPossibleSelected>
            <S.GroupShellSelectButtonDividerWrapper
              ref={selectButtonRef}
              onClick={handleFocusSubGroupShell}
              isPossibleSelected
            >
              <S.GroupShellSelectButtonDivider className="select-button divider" />
              <S.GroupShellSelectButton className="select-button button">
                SELECT
              </S.GroupShellSelectButton>
            </S.GroupShellSelectButtonDividerWrapper>
            {isDraggingSubGroupShell && (
              <S.DragButton
                data-button="drag-button"
                onClick={(e) => {
                  e.stopPropagation();
                  changeFocusStatusForDragging();
                }}
                onDragStart={(e) => {
                  handleDragStart(e, subGroupShellIndex);
                  changeFocusStatusForDragging();
                }}
                onDragEnd={() => {
                  handleDragEnd(subGroupShellKey || "");
                  changeFocusStatusForDragEnd();
                }}
                onDrag={handleDragOn}
                draggable
              >
                <TableCreatorDragButton
                  dragContainer="subGroupShell"
                  isDragging={isFocused && focusStatus.type === "dragging"}
                  onClick={(e) => {
                    e.stopPropagation();
                    changeFocusStatusForDragging();
                  }}
                />
              </S.DragButton>
            )}
            <S.GroupShell>
              {shellList.map(({ shellKey }, shellListIndex) => (
                <DealsKeyCommentCreatorShell
                  key={`shell-${String(shellListIndex)}`}
                  groupTableKey={groupTableKey}
                  groupTableIndex={groupTableIndex}
                  groupShellKey={groupShellKey}
                  groupShellIndex={groupShellIndex}
                  subGroupShellIndex={subGroupShellIndex}
                  shellKey={shellKey}
                  shellIndex={0}
                  tableKey={tableKey}
                  tableIndex={tableIndex}
                  shellCount={1}
                  isShellOverOne={false}
                  isSeparatedShellTitle={false}
                  isClearGroupShell={isClearSubGroupShell}
                  isDeleteGroupShell={isDeleteSubGroupShell}
                  isAlertGroupShell={false}
                />
              ))}
            </S.GroupShell>
          </S.SelectWrapper>
        </S.GroupShellWrapper>
      </S.GroupShellOutsideWrapper>
      {isAddDeleteSubGroupShell(articleSection, subGroupShellKey) &&
        isLastSubGroupShell && (
          <SubGroupShellDividerPlusBar
            onClick={(event) => {
              clickAddSubGroupShellButton(event, {
                tableIndexParameter: tableIndex,
                groupShellIndexParameter: groupShellIndex,
                groupShellKeyParameter: groupShellKey,
                subGroupShellIndexParameter: subGroupShellIndex + 1,
                subGroupShellKeyParameter: subGroupShellKey,
              });
            }}
            isLastPosition
            isVisibility={isFocusedAddButton}
            subGroupShellKey={subGroupShellKey}
          />
        )}
    </>
  );
};

export default DealsKeyCommentMaSubGroupShell;
